import { SpartenType, TaasTarifrisikoschluesselTHH, TaasThhHundData } from '@ps20/bff-models';
import { convertThhTaasBausteinToTarifrisikoschluessel } from '@ps20-frontend/frontend-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  parseDatum,
} from '@ps20-frontend/preis-lib';
import { IAppState } from '../../appState';
import { AnzahlTiereEnum } from '../../reducers/versicherungsnehmer.reducer';
import { selectIsNeugeschaeft } from '../../../app/shared/selectors/vertragsoptionen.selector';

export function buildTaasThhHundDataForStrecke(state: IAppState): TaasThhHundData {
  const thhData: TaasThhHundData = generateBasicTaasThhHundData(state);
  thhData.salesProducts = buildBasicSalesProductsForStrecke(
    state.produkt,
    convertThhTaasBausteinToTarifrisikoschluessel,
  );
  if (!selectIsNeugeschaeft(state)) {
    enhanceTierbausteine(state, thhData);
  }
  return thhData;
}

export function buildTaasThhHundDataForShop(state: IAppState): TaasThhHundData {
  const thhData: TaasThhHundData = generateBasicTaasThhHundData(state);
  thhData.salesProducts = buildBasicSalesProductsForShop(
    state.produkt,
    convertThhTaasBausteinToTarifrisikoschluessel,
  );
  if (!selectIsNeugeschaeft(state)) {
    thhData.tierBausteine = [];
    enhanceTierbausteine(state, thhData);
  }

  return thhData;
}

function generateBasicTaasThhHundData(state: IAppState): TaasThhHundData {
  const hasOnlyOneDog: boolean = state.versicherungsnehmer.anzahlTiere === AnzahlTiereEnum.EINES;
  return {
    spartenType: SpartenType.TIERHALTERHAFTPFLICHT_HUND,
    anzVorschaedenHund: state.vorschaeden.thhHundVorschaeden,
    tierrasse: hasOnlyOneDog
      ? [state.versicherungsnehmer.rasseEinHund]
      : state.versicherungsnehmer.rasseMehrereHunde,
    plzVN: state.risikoort.plz,
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    salesProducts: null,
    urbeginndatum: state.vertragsoptionen?.urbeginn,
    internerVertragsbezugs: state.taasInfo?.internerVertragsbezugs,
  };
}

export function addMissingTierBausteineWithoutDeltafaktor(
  state: IAppState,
  thhData: TaasThhHundData,
): void {
  let missingRassen = [...state.versicherungsnehmer.rasseMehrereHunde];
  missingRassen = missingRassen.concat(state.versicherungsnehmer.rasseEinHund);
  thhData.tierBausteine = thhData.tierBausteine.filter(tier =>
    missingRassen.includes(tier.tierRasse),
  );
  const bausteinRassen = thhData.tierBausteine.map(tier => tier.tierRasse);
  bausteinRassen.forEach(bausteinHundeRasse => {
    const index = missingRassen.indexOf(bausteinHundeRasse);
    if (index !== -1) {
      missingRassen.splice(index, 1);
    }
  });
  missingRassen = missingRassen.filter(hund => !!hund);
  const maxExistingInstanzId = getMaxExistingInstanzId(thhData);
  missingRassen.forEach((hundeRasse, index) => {
    const instanzId = maxExistingInstanzId + index + 1;
    thhData.tierBausteine.push({
      instanzId,
      tarifrisikoschluessel: TaasTarifrisikoschluesselTHH.H,
      tierRasse: hundeRasse,
      kennung: TaasTarifrisikoschluesselTHH.H + instanzId,
    });
  });
  // if one or more of dogs deleted
  const tierrasseList = thhData.tierrasse;
  thhData.tierBausteine.forEach((tierbaustein, index) => {
    const countTierrassenList = tierrasseList.filter(
      tierrasse => tierrasse === tierbaustein.tierRasse,
    ).length;
    const countTierrasseBausteine = thhData.tierBausteine.filter(
      baustein => baustein.tierRasse === tierbaustein.tierRasse,
    ).length;
    if (countTierrassenList < countTierrasseBausteine) {
      thhData.tierBausteine.splice(index, 1);
    }
  });
  thhData.tierBausteine = thhData.tierBausteine.filter(tierbaustein => !!tierbaustein);
}

function enhanceTierbausteine(state: IAppState, thhData: TaasThhHundData): void {
  thhData.tierBausteine = [];
  const deltafaktoren = state.taasInfo.deltafaktoren;
  const bausteinAnpassungsfaktoren = state.taasInfo.bausteinAnpassungsfaktoren;
  thhData.plzFaktor = state.taasInfo.deltafaktoren?.plzFaktor;
  if (state.versicherungsnehmer.tiere) {
    state.versicherungsnehmer.tiere?.forEach(tier => {
      thhData.tierBausteine.push({
        kennung: TaasTarifrisikoschluesselTHH.H + tier.instanzId,
        instanzId: tier.instanzId,
        tarifrisikoschluessel: TaasTarifrisikoschluesselTHH.H,
        tierRasse: tier.tierrasse,
        deltafaktor: deltafaktoren?.deltafaktoren?.find(
          faktor => faktor.instanzId === tier.instanzId,
        )?.faktor,
        bausteinAnpassungsfaktor: bausteinAnpassungsfaktoren?.bausteinAnpassungsfaktoren?.find(
          faktor => faktor.instanzId === tier.instanzId,
        )?.bausteinAnpassungsfaktor,
      });
    });

    const sortedRasseMehrereHunde = state.versicherungsnehmer.rasseMehrereHunde?.sort((a, b) =>
      (a ?? '').localeCompare(b ?? ''),
    );
    const sortedTierrassen = thhData.tierrasse?.sort((a, b) => (a ?? '').localeCompare(b ?? ''));

    if (
      state.versicherungsnehmer.tiere.length !== thhData.tierrasse?.length ||
      sortedRasseMehrereHunde !== sortedTierrassen
    ) {
      addMissingTierBausteineWithoutDeltafaktor(state, thhData);
    }
  } else {
    const tierTicker = thhData.tierrasse?.map(s => s);
    tierTicker?.forEach((value, index) => {
      thhData.tierBausteine.push({
        instanzId: index + 1,
        tarifrisikoschluessel: TaasTarifrisikoschluesselTHH.H,
        tierRasse: value,
      });
    });
  }
  fixInstanzIds(thhData);
}

export function fixInstanzIds(taasThhData: TaasThhHundData): void {
  const maxExistingInstanzId = getMaxExistingInstanzId(taasThhData);
  taasThhData.tierBausteine?.forEach((tierbaustein, index) => {
    if (!tierbaustein.instanzId) {
      const instanzId = maxExistingInstanzId + index + 1;
      tierbaustein.instanzId = instanzId;
      tierbaustein.kennung = TaasTarifrisikoschluesselTHH.H + instanzId;
    }
  });
}

function getMaxExistingInstanzId(taasThhData: TaasThhHundData): number {
  const result = taasThhData.tierBausteine.reduce((instanzId, tierBaustein) => {
    return Math.max(instanzId, tierBaustein.instanzId);
  }, 0);
  return result || 0;
}
